import { useMutation } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { toast } from 'sonner';
import * as React from 'react';
import { IAIAgentTemplate } from '../../../../extViewmodels/sdk/models/AI';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../web/components-v2/Alert';
import { Button } from '../../../../web/components-v2/Button';
import { Field, FieldDescription, FieldLabel, FieldTitle } from '../../../../web/components-v2/Field';
import { NumberedScaleInput } from '../../../../web/components-v2/NumberedScaleInput';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { isWrittenPersona, sessionMarkdown } from './labLogic';
import { styles } from './styles';
import type { useCoachLabCall } from './useCoachLabCall';

export const CoachLabWrapUp = ({
	call,
	coach,
	onSave,
	onCallAgain,
	onNewCall,
}: {
	call: ReturnType<typeof useCoachLabCall>;
	coach: IAIAgentTemplate;
	/** Adds this call to the sitting's list; the manager downloads the list once, at the end. */
	onSave: (session: { label: string; markdown: string }) => void;
	onCallAgain: () => void;
	onNewCall: () => void;
}) => {
	const { state } = call;
	const userSession = useUserSession();
	const [rating, setRating] = React.useState<string>();
	const [overall, setOverall] = React.useState('');
	const [discarding, setDiscarding] = React.useState(false);
	const ratingLabelId = React.useId();

	const saveMutation = useMutation({
		mutationFn: async (next: () => void) => {
			const markdown = sessionMarkdown({
				buildId: await call.buildId(),
				coach: { id: coach.id, model: coach.model, name: coach.name },
				companyId: state.companyId,
				gamePlan: state.gamePlan,
				leadLabel: state.leadLabel,
				manager: userSession.user ? getDisplayName(userSession.user) : 'unknown',
				overall,
				persona: state.persona,
				debrief: await call.debriefText(),
				mix: state.mix,
				rating: Number(rating),
				turns: state.turns,
			});
			onSave({ label: state.persona?.org ?? state.leadLabel, markdown });
			return next;
		},
		onError: () => toast.error('Could not save the call. Try again.'),
		onSuccess: next => next(),
	});

	const persona = state.persona;

	return (
		<section className={css(styles.step)}>
			<h2 className={css(styles.stepTitle)}>{state.hungUp ? 'The prospect hung up' : 'Call ended'}</h2>
			{persona ? (
				<p className={css(styles.reveal)}>
					{isWrittenPersona(persona)
						? 'This prospect was set to end the call, unless you clearly beat a typical call: '
						: 'On the real call, this person ended it: '}
					<strong>{persona.realOutcome}</strong>
				</p>
			) : null}

			{state.debrief ? (
				<div className={css(styles.tipCard)}>
					<h3 className={css(styles.tipTitle)}>Why the prospect ended it</h3>
					{state.debrief.status === 'asking' ? (
						<p className={css(styles.status)}>Asking the prospect...</p>
					) : state.debrief.status === 'done' ? (
						<p className={css(styles.help)}>{state.debrief.text}</p>
					) : (
						<p className={css(styles.help)}>The prospect didn't answer this time.</p>
					)}
				</div>
			) : null}

			<Field>
				<FieldTitle id={ratingLabelId}>How good was the coaching on this call?</FieldTitle>
				<NumberedScaleInput
					min={1}
					max={5}
					value={rating}
					onValueChange={setRating}
					lowLabel='Poor'
					highLabel='Great'
					ariaLabelledby={ratingLabelId}
				/>
				{!rating ? <FieldDescription>Pick a rating to save the call.</FieldDescription> : null}
			</Field>

			<Field>
				<FieldLabel htmlFor='coach-lab-overall'>What should the coach do differently? (optional)</FieldLabel>
				<TextArea
					styles={[styles.resizableText]}
					id='coach-lab-overall'
					placeholder='The one or two things you would change about how it coached this call'
					value={overall}
					onChange={e => setOverall(e.target.value)}
				/>
				<FieldDescription>
					Saving adds this call to Calls this sitting. When you are done, download them all in one file and send it to
					Jes.
				</FieldDescription>
			</Field>
			<div className={css(styles.actions)}>
				<Button
					variant='primary'
					disabled={!rating || saveMutation.isPending}
					isLoading={saveMutation.isPending}
					onClick={() => saveMutation.mutate(onNewCall)}
				>
					Save and call someone else
				</Button>
				<Button
					variant='secondary'
					disabled={!rating || saveMutation.isPending}
					onClick={() => saveMutation.mutate(onCallAgain)}
				>
					Save and call the same prospect again
				</Button>
				<Button variant='link' onClick={() => setDiscarding(true)}>
					Discard this call
				</Button>
			</div>
			{discarding ? (
				<Alert tone='warning'>
					<AlertTitle>Discard this call?</AlertTitle>
					<AlertDescription>Your notes and rating on it won't be saved.</AlertDescription>
					<AlertActions>
						<Button variant='outline' size='sm' onClick={() => setDiscarding(false)}>
							Keep it
						</Button>
						<Button variant='outline' size='sm' onClick={onNewCall}>
							Discard
						</Button>
					</AlertActions>
				</Alert>
			) : null}
		</section>
	);
};
